import cn from 'classnames'
import {ChangeEvent, FC, forwardRef, HTMLProps, Ref} from 'react'

import {theme} from '@/theme/theme'

type InputProps = {
  value: string | number
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  hasError?: boolean
  hasStartIcon?: boolean
  hasEndIcon?: boolean
  hasBorder?: boolean
  ref?: Ref<HTMLInputElement>
  backgroundColor?: string
  borderColor?: string
} & HTMLProps<HTMLInputElement>

export const Input: FC<React.PropsWithChildren<InputProps>> = forwardRef(
  (
    {
      value,
      onChange,
      hasError = false,
      hasStartIcon = false,
      hasEndIcon = false,
      hasBorder = true,
      color = theme.palette.text.default,
      backgroundColor = theme.palette.background.default,
      borderColor = theme.palette.gray[300],
      ...props
    },
    ref,
  ) => {
    const {type, disabled} = props

    return (
      <>
        <input
          {...props}
          ref={ref}
          value={value}
          onChange={onChange}
          disabled={disabled}
          type={type || 'text'}
          className={cn(
            'input',
            hasError && 'error',
            disabled && 'disabled',
            hasBorder && 'border',
          )}
        />

        <style jsx>{`
          .input {
            width: 100%;
            ${theme.typography.text16.desktop};
            color: ${color};
            padding-top: ${theme.spacing[2]};
            padding-bottom: ${theme.spacing[2]};
            padding-left: ${hasStartIcon
              ? `calc(${theme.spacing[5]} + ${theme.spacing[1]})`
              : '12px'};
            padding-right: ${hasEndIcon
              ? `calc(${theme.spacing[5]} + ${theme.spacing[1]})`
              : '12px'};
            border-radius: 8px;
            background-color: ${backgroundColor};
          }
          .border {
            box-shadow: inset 0px 0px 0px 1px ${borderColor};
            -webkit-appearance: none;
          }
          .input.border:not(.error, .disabled):focus {
            box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[900]},
              0px 0px 0px 4px ${theme.palette.gray[100]};
          }
          .error {
            box-shadow: inset 0px 0px 0px 1px ${theme.palette.red[700]};
          }
          .error:focus {
            box-shadow: inset 0px 0px 0px 1px ${theme.palette.error.main},
              0px 0px 0px 2px ${theme.palette.red[200]};
          }
          .disabled {
            color: ${theme.palette.gray[400]};
            background-color: ${theme.palette.gray[200]};
            box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
          }
          input[type='number']::-webkit-outer-spin-button,
          input[type='number']::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type='number'] {
            -webkit-appearance: none;
            -moz-appearance: textfield;
            margin: 0;
          }
        `}</style>
      </>
    )
  },
)
